import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { Box, Tooltip } from "@mui/material";
import { GridAddIcon } from "@mui/x-data-grid";
import { useQueryClient } from "@tanstack/react-query";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { COLORS } from "../../assets/theme";
import AutoComplete from "../../components/common/AutoComplete";
import CustomDialog from "../../components/common/CustomDialog";
import { ICustomButtonProps } from "../../components/common/CustomButton";
import CustomTable from "../../components/common/table/CustomTable";
import SearchHeaderContainer from "../../components/common/table/SearchHeaderContainer";
import ToastNotification from "../../components/ToastNotification";
import {
  addCurrencyProviderApi,
  deleteCurrencyProviderApi,
  editCurrencyProviderApi,
  fetchAllCurrencyProviders,
  ICurrencyProvider,
  ICurrencyProviderRequestField,
  useCurrencyProvidersPageQuery,
} from "../../services/CurrencyProviderService";
import { hasJwtPermission } from "../../services/ServicesHelpers";
import {
  createGlobalConfigApi,
  editGlobalConfigApi,
  IGlobalConfig,
  useGlobalConfigByTypeQuery,
} from "../../services/SystemConfigService";
import { IAction, IField, Params } from "../../types/table/interfaces.type";

// ─── Constants ────────────────────────────────────────────────────────────────

const SECONDS_PER_HOUR = 3600;

const secondsToHours = (seconds: number | undefined): number =>
  seconds !== undefined ? seconds / SECONDS_PER_HOUR : 1;

const hoursToSeconds = (hours: number | undefined): number =>
  hours !== undefined ? Math.round(hours * SECONDS_PER_HOUR) : SECONDS_PER_HOUR;

const httpMethodOptions = [
  { label: "GET", value: "GET" },
  { label: "POST", value: "POST" },
];

const contentTypeOptions = [
  { label: "application/json", value: "APPLICATION_JSON" },
  { label: "application/xml", value: "APPLICATION_XML" },
  { label: "text/plain", value: "TEXT_PLAIN" },
  { label: "application/x-www-form-urlencoded", value: "APPLICATION_FORM_URLENCODED" },
  { label: "multipart/form-data", value: "MULTIPART_FORM_DATA" },
];

const locationOptions = [
  { label: "URL", value: "URL" },
  { label: "BODY", value: "BODY" },
  { label: "HEADER", value: "HEADER" },
];

const initialProviderData: ProviderDialogData = {
  name: "",
  baseUrl: "",
  apiKey: "",
  username: "",
  password: "",
  updateIntervalHours: 1,
  apiKeyFieldName: "",
  apiKeyFieldLocation: "URL",
  usernameFieldName: "",
  usernameFieldLocation: "URL",
  passwordFieldName: "",
  passwordFieldLocation: "URL",
  baseCurrencyFieldName: "",
  baseCurrencyFieldLocation: "URL",
  targetCurrencyFieldName: "",
  targetCurrencyFieldLocation: "URL",
  httpMethod: "GET",
  contentType: "APPLICATION_JSON",
  responseRatio: "",
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CurrencyProvidersDialogRef {
  openDialog: () => void;
}

interface CurrencyProvidersDialogProps {
  onChange?: () => void;
}

interface ProviderDialogData extends Partial<ICurrencyProvider> {
  updateIntervalHours?: number;
  updateIntervalHoursRaw?: string;
  apiKeyFieldName?: string;
  apiKeyFieldLocation?: string;
  usernameFieldName?: string;
  usernameFieldLocation?: string;
  passwordFieldName?: string;
  passwordFieldLocation?: string;
  baseCurrencyFieldName?: string;
  baseCurrencyFieldLocation?: string;
  targetCurrencyFieldName?: string;
  targetCurrencyFieldLocation?: string;
  httpMethod?: string;
  contentType?: string;
  responseRatio?: string;
}

type FormMode = "add" | "edit" | "delete";

// ─── Main Management Dialog ───────────────────────────────────────────────────

const CurrencyProvidersDialog = forwardRef<
  CurrencyProvidersDialogRef,
  CurrencyProvidersDialogProps
>(({ onChange }, ref) => {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<
    Params<ICurrencyProvider> & { _forceRefresh?: number }
  >({});

  // Global config — for reading / setting the default provider
  const { data: defaultProviderConfig } = useGlobalConfigByTypeQuery("DEFAULT_CURRENCY_PROVIDER");
  const defaultProviderId = defaultProviderConfig?.value
    ? Number(defaultProviderConfig.value)
    : null;

  // Add / edit / delete form state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<FormMode>("add");
  const [formId, setFormId] = useState<string | undefined>();
  const [formRowData, setFormRowData] = useState<ICurrencyProvider | undefined>();

  // Default-provider dialog state
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);

  useImperativeHandle(ref, () => ({
    openDialog: () => {
      setAppliedFilters({});
      setDialogOpen(true);
    },
  }));

  const openForm = (mode: FormMode, id?: string, rowData?: ICurrencyProvider) => {
    setFormMode(mode);
    setFormId(id);
    setFormRowData(rowData);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormId(undefined);
    setFormRowData(undefined);
  };

  const handleRefresh = () => {
    setAppliedFilters((prev) => ({ ...prev, _forceRefresh: Date.now() }));
    queryClient.invalidateQueries({ queryKey: ["currencyProvidersPage"] });
    onChange?.();
  };

  const actions: IAction<ICurrencyProvider>[] = [
    {
      id: "edit",
      label: "Edit",
      name: "Edit",
      icon: (
        <Tooltip title="Edit">
          <EditIcon
            sx={{
              color: hasJwtPermission("PUT_CURRENCY_PROVIDER") ? "#A39455" : "#D3D3D3",
              width: 23,
            }}
          />
        </Tooltip>
      ),
      handler: (row: ICurrencyProvider) => {
        openForm("edit", String(row.id), row);
      },
      disabled: !hasJwtPermission("PUT_CURRENCY_PROVIDER"),
    },
    {
      id: "delete",
      label: "Delete",
      name: "Delete",
      icon: (
        <Tooltip title="Delete">
          <DeleteIcon
            sx={{
              color: hasJwtPermission("DELETE_CURRENCY_PROVIDER") ? "#A39455" : "#D3D3D3",
              width: 23,
            }}
          />
        </Tooltip>
      ),
      handler: (row: ICurrencyProvider) => {
        openForm("delete", String(row.id), row);
      },
      disabled: !hasJwtPermission("DELETE_CURRENCY_PROVIDER"),
    },
  ];

  const fields: IField<ICurrencyProvider>[] = [
    {
      key: "name",
      header: "Name",
      transform: (row) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {row.id === defaultProviderId ? (
            <StarIcon sx={{ fontSize: 16, color: COLORS.GOLD }} />
          ) : (
            <StarBorderIcon sx={{ fontSize: 16, color: "#D3D3D3" }} />
          )}
          {row.name ?? "-"}
        </Box>
      ),
    },
    { key: "baseUrl", header: "Base URL" },
    {
      key: "updateIntervalSeconds",
      header: "Update Interval (h)",
      transform: (row) =>
        row.updateIntervalSeconds !== undefined
          ? String(secondsToHours(row.updateIntervalSeconds))
          : "-",
    },
    { key: "createdBy", header: "Created By" },
  ];

  const managementDialogFields = [
    {
      type: "custom" as const,
      fullWidth: true,
      component: (
        <Box sx={{ width: "100%" }}>
          <SearchHeaderContainer
            search={appliedFilters.search ?? ""}
            setSearch={(value) =>
              setAppliedFilters((prev) => ({ ...prev, search: value }))
            }
            buttonConfig={[
              {
                size: "small",
                bgColor: COLORS.GOLD,
                startIcon: <StarBorderIcon />,
                onClick: () => setDefaultDialogOpen(true),
                textColor: COLORS.iconColor,
                permission: "PUT_GLOBAL_CONFIG",
              },
              {
                size: "small",
                bgColor: COLORS.GOLD,
                startIcon: <GridAddIcon />,
                onClick: () => openForm("add"),
                textColor: COLORS.iconColor,
                permission: "POST_CURRENCY_PROVIDER",
              },
            ]}
          />
          <CustomTable<ICurrencyProvider> fullBleedMobile={false}
            filters={appliedFilters}
            fields={fields}
            fetchFunc={useCurrencyProvidersPageQuery}
            actions={actions}
            sortableFields={["name"]}
          />
        </Box>
      ),
    },
  ];

  return (
    <>
      <CustomDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        mode="history"
        dialogData={{ accountType: "currencyProvider" }}
        title="Currency Providers"
        fields={managementDialogFields}
        buttons={[]}
      />

      <ProviderFormDialog
        open={formOpen}
        mode={formMode}
        id={formId}
        rowData={formRowData}
        onClose={closeForm}
        onRefresh={handleRefresh}
      />

      <DefaultProviderDialog
        open={defaultDialogOpen}
        globalConfig={defaultProviderConfig ?? null}
        currentDefaultId={defaultProviderId}
        onClose={() => setDefaultDialogOpen(false)}
        onRefresh={() => {
          queryClient.invalidateQueries({ queryKey: ["globalConfig", "DEFAULT_CURRENCY_PROVIDER"] });
          onChange?.();
        }}
      />
    </>
  );
});

CurrencyProvidersDialog.displayName = "CurrencyProvidersDialog";

export default CurrencyProvidersDialog;

// ─── Provider Form Dialog (add / edit / delete) ───────────────────────────────

interface ProviderFormDialogProps {
  open: boolean;
  mode: FormMode;
  id?: string;
  rowData?: ICurrencyProvider;
  onClose: () => void;
  onRefresh: () => void;
}

const ProviderFormDialog = ({
  open,
  mode,
  id,
  rowData,
  onClose,
  onRefresh,
}: ProviderFormDialogProps) => {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const [dialogData, setDialogData] =
    useState<ProviderDialogData>(initialProviderData);
  const [errors, setErrors] = useState<{ [key: string]: boolean }>({});
  const [currentStep, setCurrentStep] = useState(0);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const stepLabels =
    mode === "delete"
      ? ["Delete Provider"]
      : ["Provider Details", "Request Field", "Response Field"];

  // Populate form data when opening — uses row data passed directly, no extra API call
  useEffect(() => {
    if (!open) return;
    setCurrentStep(0);
    setErrors({});

    if ((mode === "edit" || mode === "delete") && rowData) {
      setDialogData({
        ...initialProviderData,
        ...rowData,
        updateIntervalHours: secondsToHours(rowData.updateIntervalSeconds),
        apiKeyFieldName: rowData.requestField?.apiKey?.name || "",
        apiKeyFieldLocation: rowData.requestField?.apiKey?.location || "URL",
        usernameFieldName: rowData.requestField?.username?.name || "",
        usernameFieldLocation: rowData.requestField?.username?.location || "URL",
        passwordFieldName: rowData.requestField?.password?.name || "",
        passwordFieldLocation: rowData.requestField?.password?.location || "URL",
        baseCurrencyFieldName: rowData.requestField?.baseCurrency?.name || "",
        baseCurrencyFieldLocation: rowData.requestField?.baseCurrency?.location || "URL",
        targetCurrencyFieldName: rowData.requestField?.targetCurrency?.name || "",
        targetCurrencyFieldLocation: rowData.requestField?.targetCurrency?.location || "URL",
        httpMethod: rowData.requestField?.httpMethod || "GET",
        contentType: rowData.requestField?.contentType || "APPLICATION_JSON",
        responseRatio: rowData.responseField?.ratio || "",
      });
    } else {
      setDialogData(initialProviderData);
    }
  }, [open, mode, id, rowData]);

  useEffect(() => {
    let hasErrors = false;
    if (currentStep === 0) {
      if (!dialogData.name?.trim()) hasErrors = true;
      if (!hasErrors && !dialogData.baseUrl?.trim()) hasErrors = true;
      if (!hasErrors && !dialogData.updateIntervalHours) hasErrors = true;
    } else if (currentStep === 1) {
      if (!dialogData.httpMethod?.trim()) hasErrors = true;
    } else if (currentStep === 2) {
      if (!dialogData.responseRatio?.trim()) hasErrors = true;
    }
    setIsSaveDisabled(hasErrors);
    setIsNextDisabled(hasErrors && currentStep < stepLabels.length - 1);
  }, [dialogData, currentStep, stepLabels.length]);

  const buildPayload = (): Partial<ICurrencyProvider> => {
    const orNull = (v: string | null | undefined) => v?.trim() || null;
    const loc = (v: string | undefined) =>
      (v as "URL" | "BODY" | "HEADER") || "URL";

    const requestField: ICurrencyProviderRequestField = {
      apiKey: {
        name: orNull(dialogData.apiKeyFieldName),
        location: loc(dialogData.apiKeyFieldLocation),
      },
      username: {
        name: orNull(dialogData.usernameFieldName),
        location: loc(dialogData.usernameFieldLocation),
      },
      password: {
        name: orNull(dialogData.passwordFieldName),
        location: loc(dialogData.passwordFieldLocation),
      },
      baseCurrency: {
        name: orNull(dialogData.baseCurrencyFieldName),
        location: loc(dialogData.baseCurrencyFieldLocation),
      },
      targetCurrency: {
        name: orNull(dialogData.targetCurrencyFieldName),
        location: loc(dialogData.targetCurrencyFieldLocation),
      },
      httpMethod: (dialogData.httpMethod as "GET" | "POST") || "GET",
      contentType: dialogData.contentType || "APPLICATION_JSON",
    };

    return {
      id: dialogData.id,
      name: dialogData.name,
      baseUrl: orNull(dialogData.baseUrl),
      apiKey: orNull(dialogData.apiKey),
      username: orNull(dialogData.username),
      password: orNull(dialogData.password),
      updateIntervalSeconds: hoursToSeconds(dialogData.updateIntervalHours),
      requestField,
      responseField: { ratio: orNull(dialogData.responseRatio) },
      version: dialogData.version,
      createdBy: dialogData.createdBy,
      updatedBy: dialogData.updatedBy,
      createdDate: dialogData.createdDate,
      timestamp: dialogData.timestamp,
    };
  };

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      if (mode === "edit" && id) {
        await editCurrencyProviderApi(id, payload);
        ToastNotification.success("Currency provider updated successfully!");
      } else if (mode === "add") {
        await addCurrencyProviderApi(payload);
        ToastNotification.success("Currency provider added successfully!");
      }
      onClose();
      onRefresh();
      setDialogData(initialProviderData);
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const handleDelete = async () => {
    try {
      if (id) {
        await deleteCurrencyProviderApi(id);
        ToastNotification.success("Currency provider deleted successfully!");
        onClose();
        onRefresh();
      }
    } catch (error: any) {
      ToastNotification.error(error.message);
    }
  };

  const handleClose = () => {
    setCurrentStep(0);
    setDialogData(initialProviderData);
    onClose();
  };

  const getStepFields = () => {
    if (mode === "delete") {
      return [
        {
          type: "custom" as const,
          component: (
            <Box>Are you sure you want to delete this Currency Provider?</Box>
          ),
        },
      ];
    }

    const steps = [
      // ── Step 0: Provider Details ──────────────────────────────────────
      [
        {
          type: "text" as const,
          label: "Name",
          placeholder: "Provider Name",
          value: dialogData.name || "",
          error: errors.name || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.name ? "Name is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, name: String(value) }));
            setErrors((prev) => ({ ...prev, name: !value }));
          },
        },
        {
          type: "text" as const,
          label: "Base URL",
          placeholder: "https://api.example.com/rates",
          value: dialogData.baseUrl || "",
          error: errors.baseUrl || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.baseUrl ? "Base URL is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, baseUrl: String(value) }));
            setErrors((prev) => ({ ...prev, baseUrl: !value }));
          },
        },
        {
          type: "text" as const,
          label: "API Key",
          placeholder: "API Key",
          value: dialogData.apiKey || "",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, apiKey: String(value) }));
          },
        },
        {
          type: "text" as const,
          label: "Username",
          placeholder: "Username",
          value: dialogData.username || "",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, username: String(value) }));
          },
        },
        {
          type: "text" as const,
          label: "Password",
          placeholder: "Password",
          value: dialogData.password || "",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({ ...prev, password: String(value) }));
          },
        },
        {
          type: "text" as const,
          label: "Update Interval (hours)",
          placeholder: "e.g. 1",
          value:
            dialogData.updateIntervalHoursRaw !== undefined
              ? dialogData.updateIntervalHoursRaw
              : dialogData.updateIntervalHours !== undefined
              ? String(dialogData.updateIntervalHours)
              : "",
          error: errors.updateIntervalHours || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          helperText: errors.updateIntervalHours
            ? "Update interval is required."
            : "",
          tooltip: "How often to fetch the exchange rate from the API",
          onChange: (value: string | number) => {
            const raw = String(value);
            const num = Number.parseFloat(raw);
            const invalid =
              Number.isNaN(num) || raw.trim() === "" || num <= 0;
            setDialogData((prev) => ({
              ...prev,
              updateIntervalHoursRaw: raw,
              updateIntervalHours: Number.isNaN(num) ? undefined : num,
            }));
            setErrors((prev) => ({
              ...prev,
              updateIntervalHours: invalid,
            }));
          },
        },
      ],

      // ── Step 1: Request Field ─────────────────────────────────────────
      [
        {
          type: "text" as const,
          label: "API Key Field Name",
          placeholder: "e.g. api_key",
          value: dialogData.apiKeyFieldName || "",
          bgColor: COLORS.WhiteBackground,
          tooltip: "The name of the API key parameter in the request",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              apiKeyFieldName: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "API Key Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.apiKeyFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              apiKeyFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Username Field Name",
          placeholder: "e.g. username",
          value: dialogData.usernameFieldName || "",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              usernameFieldName: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "Username Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.usernameFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              usernameFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Password Field Name",
          placeholder: "e.g. password",
          value: dialogData.passwordFieldName || "",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              passwordFieldName: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "Password Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.passwordFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              passwordFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Base Currency Field Name",
          placeholder: "e.g. base_currency",
          value: dialogData.baseCurrencyFieldName || "",
          bgColor: COLORS.WhiteBackground,
          tooltip: "The name of the base currency parameter in the request",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              baseCurrencyFieldName: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "Base Currency Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.baseCurrencyFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              baseCurrencyFieldLocation: String(value),
            }));
          },
        },
        {
          type: "text" as const,
          label: "Target Currency Field Name",
          placeholder: "e.g. target_currency",
          value: dialogData.targetCurrencyFieldName || "",
          bgColor: COLORS.WhiteBackground,
          tooltip: "The name of the target currency parameter in the request",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              targetCurrencyFieldName: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "Target Currency Location",
          placeholder: "Select Location",
          options: locationOptions,
          value: dialogData.targetCurrencyFieldLocation || "URL",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              targetCurrencyFieldLocation: String(value),
            }));
          },
        },
        {
          type: "select" as const,
          label: "HTTP Method",
          placeholder: "Select Method",
          options: httpMethodOptions,
          value: dialogData.httpMethod || "GET",
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          error: errors.httpMethod || false,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              httpMethod: String(value),
            }));
            setErrors((prev) => ({ ...prev, httpMethod: !value }));
          },
        },
        {
          type: "select" as const,
          label: "Content Type",
          placeholder: "Select Content Type",
          options: contentTypeOptions,
          value: dialogData.contentType || "APPLICATION_JSON",
          bgColor: COLORS.WhiteBackground,
          onChange: (value: string | number | (string | number)[]) => {
            setDialogData((prev) => ({
              ...prev,
              contentType: String(value),
            }));
          },
        },
      ],

      // ── Step 2: Response Field ────────────────────────────────────────
      [
        {
          type: "text" as const,
          label: "Ratio Field",
          placeholder: "e.g. rates.reference_currency",
          value: dialogData.responseRatio || "",
          error: errors.responseRatio || false,
          bgColor: COLORS.WhiteBackground,
          highlightLabelStar: true,
          tooltip:
            "The JSON field name in the API response that contains the exchange ratio",
          helperText: errors.responseRatio ? "Ratio field is required." : "",
          onChange: (value: string | number) => {
            setDialogData((prev) => ({
              ...prev,
              responseRatio: String(value),
            }));
            setErrors((prev) => ({ ...prev, responseRatio: !value }));
          },
        },
        {
          type: "text" as const,
          hidden: true,
          value: "",
          onChange: () => {},
        },
      ],
    ];

    return steps[currentStep] || [];
  };

  const dialogButtons: ICustomButtonProps[] =
    mode === "delete"
      ? [
          {
            label: "Delete",
            onClick: handleDelete,
            variant: "contained",
            bgColor: COLORS.GOLD,
            permission: "DELETE_CURRENCY_PROVIDER",
          },
        ]
      : [
          ...(currentStep > 0
            ? [
                {
                  label: "Back",
                  onClick: () => setCurrentStep((prev) => prev - 1),
                  bgColor: COLORS.GOLD,
                },
              ]
            : []),
          ...(currentStep < stepLabels.length - 1
            ? [
                {
                  label: "Next",
                  onClick: () => setCurrentStep((prev) => prev + 1),
                  bgColor: COLORS.GOLD,
                  disabled: isNextDisabled,
                },
              ]
            : [
                {
                  label: "Finish",
                  onClick: handleSave,
                  bgColor: COLORS.GOLD,
                  disabled:
                    isSaveDisabled ||
                    (mode === "edit"
                      ? !hasJwtPermission("PUT_CURRENCY_PROVIDER")
                      : !hasJwtPermission("POST_CURRENCY_PROVIDER")),
                },
              ]),
        ];

  const getTitle = () => {
    if (mode === "delete") return "Delete Currency Provider";
    if (mode === "edit") return "Edit Currency Provider";
    return "Add Currency Provider";
  };

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      mode={mode}
      dialogData={{ accountType: "currencyProvider" }}
      {...(mode !== "delete"
        ? {
            onNext: () =>
              setCurrentStep((prev) =>
                prev < stepLabels.length - 1 ? prev + 1 : prev
              ),
            onBack: () =>
              setCurrentStep((prev) => (prev > 0 ? prev - 1 : prev)),
            stepLabels,
            activeStep: currentStep,
          }
        : {})}
      title={getTitle()}
      fields={getStepFields()}
      fieldsPerRow={mode === "delete" ? undefined : 2}
      buttons={dialogButtons}
    />
  );
};

// ─── Default Provider Dialog ──────────────────────────────────────────────────

interface DefaultProviderDialogProps {
  open: boolean;
  globalConfig: IGlobalConfig | null;
  currentDefaultId: number | null;
  onClose: () => void;
  onRefresh: () => void;
}

const DefaultProviderDialog = ({
  open,
  globalConfig,
  currentDefaultId,
  onClose,
  onRefresh,
}: DefaultProviderDialogProps) => {
  const [selectedId, setSelectedId] = useState<number | "">(
    currentDefaultId ?? ""
  );
  const [providerOptions, setProviderOptions] = useState<
    { label: string; value: number }[]
  >([]);
  const [isSaving, setIsSaving] = useState(false);

  // Sync selection when the dialog opens or currentDefaultId changes
  useEffect(() => {
    if (!open) return;
    setSelectedId(currentDefaultId ?? "");
    fetchAllCurrencyProviders()
      .then((providers) =>
        setProviderOptions(providers.map((p) => ({ label: p.name, value: p.id })))
      )
      .catch(() => setProviderOptions([]));
  }, [open, currentDefaultId]);

  const handleSave = async () => {
    if (selectedId === "") return;
    setIsSaving(true);
    try {
      if (globalConfig) {
        await editGlobalConfigApi(globalConfig.id, {
          type: "DEFAULT_CURRENCY_PROVIDER",
          value: String(selectedId),
          version: globalConfig.version,
        });
      } else {
        await createGlobalConfigApi({
          type: "DEFAULT_CURRENCY_PROVIDER",
          value: String(selectedId),
        });
      }
      ToastNotification.success("Default currency provider updated successfully!");
      onClose();
      onRefresh();
    } catch (error: any) {
      ToastNotification.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const dialogFields = [
    {
      type: "custom" as const,
      component: (
        <AutoComplete
          label="Default Currency Provider"
          value={selectedId}
          options={providerOptions}
          onChange={(value) =>
            setSelectedId(value !== "" && value !== null ? Number(value) : "")
          }
          clearable
          placeholder="Select Default Provider"
        />
      ),
    },
  ];

  const dialogButtons: ICustomButtonProps[] = [
    {
      label: "Save",
      onClick: handleSave,
      bgColor: COLORS.GOLD,
      disabled: isSaving || selectedId === "",
      permission: "PUT_GLOBAL_CONFIG",
    },
  ];

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      mode="edit"
      dialogData={{ accountType: "defaultProvider" }}
      title="Set Default Currency Provider"
      fields={dialogFields}
      buttons={dialogButtons}
    />
  );
};
